import type { GrammarExercise } from '../exercise-types';

// Exercises for the Modals section (plus the two modal topics that live in
// other content files: making-predictions and past-modal-verbs). Written in
// E2E's voice — women protagonists, real work and life situations — and
// calibrated to the founder-approved pilot in ../exercises.ts.
export const modalsExercises: Record<string, GrammarExercise[]> = {
  'making-polite-requests': [
    {
      kind: 'multiple-choice',
      prompt: 'Marina is leading her first meeting in English: “___ you repeat that, please?”',
      options: ['Could', 'Do', 'Must'],
      correctIndex: 0,
      explanation: 'Could you and would you are polite ways of asking someone to do something.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“___ you like to join our women’s book club? We meet every Thursday.”',
      options: ['Could', 'Would', 'May'],
      correctIndex: 1,
      explanation: 'We use would you like (to) for invitations.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'A new colleague looks lost on her first day. You offer: “___ I help you?”',
      options: ['Should', 'Would', 'Can'],
      correctIndex: 2,
      explanation: 'We use can I or may I to make offers.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Would you ___ another coffee while we review the project?',
      answers: ['like'],
      explanation: 'Would you like is the polite structure for invitations and offers of food and drink.',
    },
    {
      kind: 'fill-blank',
      prompt: '___ you carry this box for me, please? (polite request starting with W)',
      answers: ['would'],
      explanation: 'Would you + verb is a polite way of asking someone to do something for you.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which is the most polite way to ask a colleague to lower the music?',
      options: [
        'Turn that music down.',
        'Could you turn that music down, please?',
        'You must turn that music down.',
      ],
      correctIndex: 1,
      explanation: 'Could you + please turns an order into a polite request.',
    },
    {
      kind: 'matching',
      instruction: 'Match each sentence with what the speaker is doing:',
      pairs: [
        { left: 'Could you take a message, please?', right: 'asking someone to do something' },
        { left: 'Would you like to come round tomorrow?', right: 'making an invitation' },
        { left: 'Can I do that for you?', right: 'making an offer' },
      ],
      explanation: 'Could/would you = requests, would you like = invitations, can/may I = offers.',
    },
  ],

  'modal-verbs-overview': [
    {
      kind: 'multiple-choice',
      prompt: 'Judging by the clouds, it ___ rain today — take an umbrella to your interview.',
      options: ['must', 'might', 'should'],
      correctIndex: 1,
      explanation: 'When something is possible but not certain, we use might, may, could, or can.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'In a formal meeting, Helena asks permission to leave: “___ I leave early today?”',
      options: ['Must', 'Should', 'May'],
      correctIndex: 2,
      explanation: 'For permission we use can, may, or could — and may is the most formal and polite.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'You ___ try that online course — it completely changed my career.',
      options: ['should', 'must not', 'may not'],
      correctIndex: 0,
      explanation: 'Should gives a suggestion or advice without ordering someone around.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Teresa ___ speak three languages: Portuguese, English, and Spanish. (ability, one word)',
      answers: ['can'],
      explanation: 'The modal verb can shows that the subject is able to do something.',
    },
    {
      kind: 'fill-blank',
      prompt: 'You ___ do your homework every week — your future self will thank you. (strong necessity)',
      answers: ['must', 'have to'],
      explanation: 'For a strong necessity, something that needs to be done, we use must or have to.',
    },
    {
      kind: 'matching',
      instruction: 'Match each modal verb with the idea it expresses:',
      pairs: [
        { left: 'might', right: 'possibility' },
        { left: 'can', right: 'ability' },
        { left: 'may I', right: 'asking permission' },
        { left: 'should', right: 'suggestion' },
        { left: 'must', right: 'necessity' },
      ],
      explanation: 'Modal verbs are special because each one carries its own meaning.',
    },
  ],

  'modals-of-ability': [
    {
      kind: 'multiple-choice',
      prompt: 'After two years of classes, Vera ___ lead meetings in English with confidence.',
      options: ['can', 'cans', 'can to'],
      correctIndex: 0,
      explanation: 'Can never changes form and is followed by the verb in the base form, without to.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Next year, after more practice, we ___ give full presentations in English.',
      options: ['can', 'will be able to', 'could'],
      correctIndex: 1,
      explanation: 'Be able to can be conjugated in the future — will be able to — while can only works in the present.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'They ___ find a solution to the challenging puzzle after hours of teamwork.',
      options: ['are able to', 'can', 'were able to'],
      correctIndex: 2,
      explanation: 'For a past ability, be able to goes to the past: was/were able to.',
    },
    {
      kind: 'fill-blank',
      prompt: 'She’s ___ to manage multiple projects simultaneously — organization is her superpower.',
      answers: ['able'],
      explanation: 'Be able to also talks about skills: She is able to = She can.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I ___ ride a bike — I never learned it, but it’s never too late to start! (negative of can)',
      answers: ['can’t', "can't", 'cannot', 'can not'],
      explanation: 'The negative form, cannot or can’t, shows that the subject is unable to do something.',
    },
    {
      kind: 'matching',
      instruction: 'Match each time with the correct form of be able to:',
      pairs: [
        { left: 'present', right: 'am / is / are able to' },
        { left: 'past', right: 'was / were able to' },
        { left: 'future', right: 'will be able to' },
      ],
      explanation: 'Unlike can, be able to can be conjugated in the present, past, or future.',
    },
  ],

  'modals-of-past-ability': [
    {
      kind: 'multiple-choice',
      prompt: 'Two years ago I ___ speak on the phone in English — now I do it every day!',
      options: ['can’t', 'couldn’t', 'don’t could'],
      correctIndex: 1,
      explanation: 'For abilities we didn’t have in the past, we use could not → couldn’t.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which question about a past ability is correct?',
      options: [
        'Did you could play chess last year?',
        'Could you play chess last year?',
        'You could play chess last year?',
      ],
      correctIndex: 1,
      explanation: 'To make questions, we invert the order of could and the subject.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Could your mother drive a car when she was young? No, she ___.',
      options: ['didn’t', 'can’t', 'couldn’t'],
      correctIndex: 2,
      explanation: 'Short answers about past ability use could or couldn’t.',
    },
    {
      kind: 'fill-blank',
      prompt: 'When she was four, Alice ___ already read — her mother read to her every night. (past of can)',
      answers: ['could'],
      explanation: 'Could is the past of can: could + verb in the base form.',
    },
    {
      kind: 'fill-blank',
      prompt: 'My grandmother ___ not study when she was young — so she made sure all her daughters did.',
      answers: ['could'],
      explanation: 'The negative form is could not, or the contraction couldn’t.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Before the course, Renata couldn’t ___ (write) emails in English; now she writes them daily.',
      answers: ['write'],
      explanation: 'After could or couldn’t, the verb stays in the base form.',
    },
  ],

  'modals-of-advice': [
    {
      kind: 'multiple-choice',
      prompt: 'Your friend does all the housework alone. “You ___ ask for help — it’s not all on you.”',
      options: ['had', 'should', 'would'],
      correctIndex: 1,
      explanation: 'We use should and shouldn’t to make suggestions and give advice.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence includes a warning that something bad will happen?',
      options: [
        'You should back up your files.',
        'You’d better back up your files.',
        'Why don’t you back up your files?',
      ],
      correctIndex: 1,
      explanation: 'Had better expresses a stronger suggestion with the idea of a warning.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'How about ___ a women’s networking event this month?',
      options: ['attend', 'to attend', 'attending'],
      correctIndex: 2,
      explanation: 'How about is an informal suggestion followed by the -ing form: How about watching…?',
    },
    {
      kind: 'fill-blank',
      prompt: 'You ought ___ accept yourself exactly as you are.',
      answers: ['to'],
      explanation: 'Unlike should, ought is always followed by to: you ought to.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Why ___ you apply for the senior position? You’re more than ready.',
      answers: ['don’t', "don't", 'do not'],
      explanation: 'Why don’t you…? is an informal way of suggesting something to someone.',
    },
    {
      kind: 'matching',
      instruction: 'Match each expression with its strength or style:',
      pairs: [
        { left: 'You should…', right: 'advice or suggestion' },
        { left: 'You’d better…', right: 'strong advice with a warning' },
        { left: 'Why don’t you…?', right: 'informal suggestion' },
      ],
      explanation: 'Should advises, had better warns, and why don’t you suggests informally.',
    },
  ],

  'modals-of-necessity': [
    {
      kind: 'multiple-choice',
      prompt: 'Lab rule on Rosa’s first day as supervisor: “Everyone ___ wear safety glasses at all times.”',
      options: ['should', 'must', 'might'],
      correctIndex: 1,
      explanation: 'Must is the strongest and most serious modal verb of necessity.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The report is optional this week — you ___ send it if you’re too busy.',
      options: ['mustn’t', 'must', 'don’t have to'],
      correctIndex: 2,
      explanation: 'Don’t have to means it is not necessary; mustn’t means it is prohibited.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“She’s gotta stop doubting herself” is the informal spoken form of:',
      options: [
        'She has got to stop doubting herself.',
        'She should stop doubting herself.',
        'She might stop doubting herself.',
      ],
      correctIndex: 0,
      explanation: 'Have got to (you’ve gotta) expresses necessity in informal speech.',
    },
    {
      kind: 'fill-blank',
      prompt: 'You ___ drive and text at the same time — it’s dangerous. (prohibition)',
      answers: ['mustn’t', "mustn't", 'must not'],
      explanation: 'In the negative, mustn’t expresses prohibition.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I have ___ to pay my phone bill soon. (informal necessity)',
      answers: ['got'],
      explanation: 'Have got to is the informal way of saying have to.',
    },
    {
      kind: 'matching',
      instruction: 'Match each form with its meaning:',
      pairs: [
        { left: 'must', right: 'strong necessity' },
        { left: 'mustn’t', right: 'prohibition' },
        { left: 'don’t have to', right: 'not necessary' },
        { left: 'have got to', right: 'informal necessity' },
      ],
      explanation: 'The affirmatives express necessity — but the negatives mean very different things.',
    },
  ],

  'modals-of-possibility': [
    {
      kind: 'multiple-choice',
      prompt: 'Where is Camila? She ___ be in another meeting — her calendar is full today.',
      options: ['should', 'might', 'would'],
      correctIndex: 1,
      explanation: 'When something is possible but not certain, we use may, might, or could.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'In “She could finish the project tonight”, could expresses:',
      options: ['past ability', 'permission', 'possibility'],
      correctIndex: 2,
      explanation: 'Could can mean possibility or past ability — always consider the context.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'What is the real difference between may, might, and could for possibility?',
      options: [
        'Basically none — context matters most',
        'May is only for the past',
        'Could is only for ability',
      ],
      correctIndex: 0,
      explanation: 'The three modals express possibility; any difference depends on the variety of English.',
    },
    {
      kind: 'fill-blank',
      prompt: 'We might ___ go to the conference — we had an exhausting month. (make it negative)',
      answers: ['not'],
      explanation: 'To form the negative of these modals, we simply add not: might not, may not, could not.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Judging by her preparation, Bia ___ get the promotion this quarter. (possible, starts with m)',
      answers: ['may', 'might'],
      explanation: 'May and might both express that something is possible but not certain.',
    },
    {
      kind: 'matching',
      instruction: 'Match each sentence with what could means in it:',
      pairs: [
        { left: 'She could lead the team next year.', right: 'possibility' },
        { left: 'She could read when she was four.', right: 'past ability' },
        { left: 'Could you help me with this?', right: 'polite request' },
      ],
      explanation: 'The same modal verb changes meaning with the context.',
    },
  ],

  'making-predictions': [
    {
      kind: 'multiple-choice',
      prompt: 'Look at those dark clouds — it ___ rain before the workshop starts.',
      options: ['will to', 'is going to', 'won’t be'],
      correctIndex: 1,
      explanation: 'For an objective prediction based on something we see now, we use going to, not will.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I think more women ___ reach leadership positions next year.',
      options: ['will', 'wills', 'will to'],
      correctIndex: 0,
      explanation: 'For what we think will happen, we use will followed by the verb without to.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which expression means something is certain to happen?',
      options: ['likely to', 'possible', 'sure to'],
      correctIndex: 2,
      explanation: 'Sure to and bound to mean certain; likely to only means probable.',
    },
    {
      kind: 'fill-blank',
      prompt: 'They are bound ___ succeed — they’ve worked so hard for this.',
      answers: ['to'],
      explanation: 'Bound to means certain: They are bound to succeed!',
    },
    {
      kind: 'fill-blank',
      prompt: 'An election is ___ next year, the papers say. (probable, starts with l)',
      answers: ['likely'],
      explanation: 'Likely means probable: An election is likely next year.',
    },
    {
      kind: 'matching',
      instruction: 'Match each expression with its degree of certainty:',
      pairs: [
        { left: 'bound to happen', right: 'certain' },
        { left: 'likely to happen', right: 'probable' },
        { left: 'might happen', right: 'possible, not sure' },
        { left: 'definitely won’t happen', right: 'certain it won’t' },
      ],
      explanation: 'English has a whole scale of certainty, from bound to all the way down to definitely won’t.',
    },
  ],

  'past-modal-verbs': [
    {
      kind: 'multiple-choice',
      prompt: 'Why wasn’t Marta at the meeting? She ___ forgotten — she’s been so busy.',
      options: ['might have', 'might has', 'might had'],
      correctIndex: 0,
      explanation: 'Past modals combine the modal with have + participle: might have forgotten.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The presentation was flawless. She ___ practiced for weeks.',
      options: ['should have', 'must have', 'wouldn’t have'],
      correctIndex: 1,
      explanation: 'Must have indicates deduction — you have reasons to believe it happened that way.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“She can’t have driven there — her car keys are still here.” This means:',
      options: [
        'It isn’t possible that she drove there',
        'She didn’t know how to drive',
        'She wasn’t allowed to drive there',
      ],
      correctIndex: 0,
      explanation: 'Can’t have means it isn’t possible, no matter what, judging from the present.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I ___ have negotiated my first salary — now I know my worth. (regret, starts with sh)',
      answers: ['should'],
      explanation: 'Should have indicates a past necessity or a regret: it was a good idea, but you didn’t do it.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Ana ___ have given a great speech at the conference, but she wasn’t invited to speak. (volition, starts with w)',
      answers: ['would'],
      explanation: 'Would have indicates past volition — the willingness to do something was there.',
    },
    {
      kind: 'matching',
      instruction: 'Match each past modal with its meaning:',
      pairs: [
        { left: 'must have', right: 'deduction from evidence' },
        { left: 'should have', right: 'regret about the past' },
        { left: 'might have', right: 'past possibility or guess' },
        { left: 'couldn’t have', right: 'impossible, no matter what' },
      ],
      explanation: 'The modal before have + participle tells you if it’s a guess, a regret, or a deduction.',
    },
  ],
};
